import React, { useState, useEffect, useContext } from 'react';
import { DContext } from '../../../context/DataContext';
import LoadingPage from '../Loading';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BsEye, BsPencil } from 'react-icons/bs';
import AdminListFooter from '../../blocks/AdminListFooter';

function Bookings() {
    const { t } = useTranslation();
    const { BeURL, setLoader } = useContext(DContext);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterPayment, setFilterPayment] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Fetch bookings from API
    useEffect(() => {
        if (BeURL) {
            setLoading(true);
            setLoader(true);
            fetch(`${BeURL}/fetch-bookings`, { credentials: 'include' })
                .then(res => res.json())
                .then(data => {
                    setLoader(false);
                    setLoading(false);
                    if (data.success) {
                        setBookings(Array.isArray(data.bookings) ? data.bookings : []);
                    } else {
                        alert(t('adminCommon.requestFailed'));
                    }
                })
                .catch(err => {
                    setLoader(false);
                    setLoading(false);
                    console.error("Error fetching bookings:", err);
                    alert(t('adminCommon.serverError'));
                });
        }
    }, [BeURL, setLoader, t]);

    // Filter bookings based on search query and selected filters
    const filteredBookings = bookings.filter(booking => {
        // Search: check if any booking field (converted to string) contains the search query
        const searchStr = JSON.stringify(booking).toLowerCase();
        const matchesSearch = searchStr.includes(searchQuery.toLowerCase());

        // Filter by status (if filter is selected)
        const matchesStatus = filterStatus
            ? booking.status.toLowerCase() === filterStatus.toLowerCase()
            : true;

        // Filter by payment mode (if filter is selected)
        const matchesPayment = filterPayment
            ? booking.paymentMode.toLowerCase() === filterPayment.toLowerCase()
            : true;

        return matchesSearch && matchesStatus && matchesPayment;
    });

    const totalItems = filteredBookings.length;
    const totalPages = Math.max(Math.ceil(totalItems / itemsPerPage), 1);
    const paginatedBookings = filteredBookings.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePrevious = () => {
        setCurrentPage((page) => Math.max(page - 1, 1));
    };

    const handleNext = () => {
        setCurrentPage((page) => Math.min(page + 1, totalPages));
    };

    const updateBooking = (id) => {
        if (id) {
            setLoader(true)
            fetch(`${BeURL}/update-booking/${id}`, { credentials: 'include' })
                .then(res => res.json())
                .then(data => {
                    setLoader(false)
                    if (data.success) {
                        alert(t('bookings.updateSuccess'));
                        window.location.reload()
                    } else {
                        alert(t('bookings.updateFailure'));
                    }
                })
                .catch(err => {
                    setLoader(false)
                    console.log("Error updating booking:", err)
                    alert(t('adminCommon.serverError'))
                })
        }
    }

    if (loading) {
        return <LoadingPage />
    }

    return (
        <div className="space-y-4">
            <h2 className='text-2xl font-extrabold tracking-tight text-[#1739bd]'>{t('bookings.heading')}</h2>
            {/* Search and Filters */}
            <div className="grid gap-3 md:grid-cols-3">
                <div>
                    <input
                        type="text"
                        className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                        placeholder={t('bookings.searchPlaceholder')}
                        value={searchQuery}
                        onChange={e => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1);
                        }}
                    />
                </div>
                <div>
                    <select
                        className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                        value={filterStatus}
                        onChange={e => {
                            setFilterStatus(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="">{t('bookings.filterStatus')}</option>
                        <option value="booked">{t('bookings.status.booked')}</option>
                        <option value="cancelled">{t('bookings.status.cancelled')}</option>
                    </select>
                </div>
                <div>
                    <select
                        className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                        value={filterPayment}
                        onChange={e => {
                            setFilterPayment(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="">{t('bookings.filterPayment')}</option>
                        <option value="online">{t('online')}</option>
                        <option value="offline">{t('offline')}</option>
                    </select>
                </div>
            </div>
            {/* Bookings Table */}
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-[0_14px_34px_rgba(15,23,42,0.06)]">
                <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-100/80">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('bookings.table.id')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('bookings.table.fullName')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('bookings.table.email')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('bookings.table.startDate')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('bookings.table.endDate')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('bookings.table.paymentMode')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('bookings.table.status')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('bookings.table.actions')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {paginatedBookings.length > 0 ? (
                            paginatedBookings.map(booking => (
                                <tr key={booking.id}>
                                    <td className="px-4 py-3 text-sm text-slate-600">{booking.id}</td>
                                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{booking.fullName}</td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{booking.email}</td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{new Date(booking.startDate).toLocaleDateString()}</td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{booking.endDate ? new Date(booking.endDate).toLocaleDateString() : '-'}</td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{booking.paymentMode === 'online' ? t('online') : booking.paymentMode === 'offline' ? t('offline') : booking.paymentMode}</td>
                                    <td className={`px-4 py-3 text-sm font-bold ${booking.status === "booked" ? 'text-emerald-600' : 'text-rose-600'}`}>
                                        {booking.status === 'booked' ? t('bookings.status.booked') : booking.status === 'cancelled' ? t('bookings.status.cancelled') : booking.status}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <Link
                                                to={`/admin/booking/${booking.id}`}
                                                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-indigo-200 text-[#1739bd] transition hover:bg-indigo-50"
                                                title={t('bookings.table.view')}
                                            >
                                                <BsEye />
                                            </Link>
                                            <button
                                                type="button"
                                                className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-100"
                                                title={t('bookings.table.update')}
                                                onClick={() => updateBooking(booking.id)}
                                            >
                                                <BsPencil />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="px-4 py-8 text-center text-sm text-slate-500">{t('bookings.noResults')}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <AdminListFooter
                totalItems={totalItems}
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                }}
                onPrevious={handlePrevious}
                onNext={handleNext}
            />
        </div>
    );
}

export default Bookings;
